/**
 * Instances API — the server check an instance row goes through before it
 * joins the portfolio.
 *
 * The legacy call (apiservice.js:466-475, ManageAccount.vue:659-701):
 *   POST /input/validate
 *     { provider, appName: 'CCA'|'EIA', model?: 'Scaler', data: [row, ...] }
 *   -> { Data: [row, ...] }            each row echoed back with its `Remarks`
 *      or { ErrorCode: -1, Message }   a failure on an HTTP 200
 *
 * `Remarks` is a list of { Field, Message } — per-cell problems the table
 * shows under the cell. A row WITH remarks is still added (legacy does too);
 * only a failed call or a failure envelope stops it.
 *
 * A mutation: every Add must reach the server, never a cached answer.
 *
 * Find and Replace (legacy ActionButtons.vue handlereplaceAll :265-326,
 * apiservice.js bulkUpdateAPI :529-539):
 *   POST /input/correct
 *     { provider, cloud_csp, appName, model?: 'Scaler',
 *       instanceType: { from, to }, region: { from, to }, pricingModel: { from, to },
 *       data: [every row] }
 *   -> { Data: [every row, corrected and re-checked] }
 * All three pairs are always sent; '' means "blank cells" in `from` and an
 * unused pair as { from: '', to: '' }. The SERVER does the replacing.
 */
import { appNameFor } from '@services/apiClient';
import { API_ENDPOINTS } from '@services/apiEndpoints';
import { envelopeError, isFailureEnvelope } from '@services/apiMessages';
import { baseApi } from '@services/baseApi';

const { VALIDATE_INSTANCES, CORRECT_INSTANCES } = API_ENDPOINTS.MANAGE_PORTFOLIO;

export const VALIDATE_FAILED_MESSAGE = 'Could not validate the instance. Please try again.';
export const CORRECT_FAILED_MESSAGE = 'Could not replace the values. Please try again.';

/**
 * The validated rows out of a response — or THROW, so the caller's catch block
 * handles a failure envelope exactly like an HTTP error.
 *
 * @returns {object[]} at least one row
 */
export function readValidatedRows(response) {
  if (isFailureEnvelope(response)) throw envelopeError(response, VALIDATE_FAILED_MESSAGE);
  const rows = response?.Data;
  if (!Array.isArray(rows) || rows.length === 0) {
    throw envelopeError(response, VALIDATE_FAILED_MESSAGE);
  }
  return rows;
}

/**
 * Find and Replace's reply -> its rows, or THROW. Legacy did not check: a
 * failed call set the table to `undefined` -> [] and WIPED every instance.
 */
export function readCorrectedRows(response) {
  if (isFailureEnvelope(response)) throw envelopeError(response, CORRECT_FAILED_MESSAGE);
  const rows = response?.Data;
  if (!Array.isArray(rows) || rows.length === 0) {
    throw envelopeError(response, CORRECT_FAILED_MESSAGE);
  }
  return rows;
}

export const instancesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    validateInstances: builder.mutation({
      query: ({ provider, model, rows }) => ({
        url: VALIDATE_INSTANCES,
        method: 'POST',
        body: {
          provider,
          appName: appNameFor(),
          // Legacy sends model only for the Scaler costing path.
          ...(model ? { model } : {}),
          data: rows,
        },
      }),
    }),

    correctInstances: builder.mutation({
      query: ({ provider, model, pairs, rows }) => ({
        url: CORRECT_INSTANCES,
        method: 'POST',
        body: {
          provider,
          cloud_csp: provider,
          appName: appNameFor(),
          ...(model ? { model } : {}),
          instanceType: pairs.instanceType,
          region: pairs.region,
          pricingModel: pairs.pricingModel,
          data: rows,
        },
      }),
    }),
  }),
});

export const { useValidateInstancesMutation, useCorrectInstancesMutation } = instancesApi;
